import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

export enum ThemeMode {
  LIGHT = "light",
  DARK = "dark",
  SYSTEM = "system",
}
type BsTheme = "light" | "dark";

/**
 * @param forcedTheme Thème imposé par une mise en page, quel que soit le choix
 * de l'utilisateur (rapport/terrain en clair, outil dessin en sombre). Le choix
 * de l'utilisateur est réappliqué quand la mise en page est démontée.
 */
export const useTheme = (forcedTheme?: BsTheme) => {
  const theme = ref(ThemeMode.SYSTEM);

  const applyTheme = (value: ThemeMode, forced = forcedTheme ?? null) => {
    let dataBsTheme: BsTheme;

    if (forced) {
      dataBsTheme = forced;
    } else if (value === ThemeMode.LIGHT) {
      dataBsTheme = "light";
    } else if (value === ThemeMode.SYSTEM) {
      const isSystemThemeDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      );
      dataBsTheme = isSystemThemeDark.matches ? "dark" : "light";
    } else {
      dataBsTheme = "dark";
    }

    document.documentElement.setAttribute("data-bs-theme", dataBsTheme);
  };

  const setTheme = (value: ThemeMode) => {
    theme.value = value;
    applyTheme(value);
    $ls.setItem(LocalStorageKey.THEME_MODE, value);
  };

  onMounted(() => {
    const savedTheme = $ls.getItem(LocalStorageKey.THEME_MODE);
    setTheme(savedTheme ? (savedTheme as ThemeMode) : ThemeMode.SYSTEM);
  });

  if (forcedTheme) {
    onBeforeUnmount(() => applyTheme(theme.value, null));
  }

  watch(theme, (newValue) => {
    $ls.setItem(LocalStorageKey.THEME_MODE, newValue);
  });

  return {
    theme,
    setTheme,
  };
};
